import {
  Grid,
  GridItem,
  GridItemProps,
  Text,
  HStack,
  Tooltip,
  Hide,
  Spinner,
} from "@chakra-ui/react";

import { Decimal } from "@syla/shared/decimal";
import { getAssetName } from "@syla/shared/helpers/assets/getAssetName";
import { getAssetShortName } from "@syla/shared/helpers/assets/getAssetShortName";
import { PopulatedAssetHolding } from "@syla/shared/types/models/AssetHoldingBase";
import React from "react";
import { NumberStyler } from "../../../../../helper/NumberStyler";
import { useValueHoldings } from "../../../../../store/useValueHoldings";
import { ImageWithMissingSrc } from "../../../../atoms/ImageWithMissingSrc";
import { InfoToolTip } from "../../../../atoms/InfoToolTip";
import { SkeletonRect } from "../../../../atoms/Skeletons";
import { sortHoldings } from "../../sortHoldings";

export const HoldingTable = ({
  holdings,
  totalHoldingValue,
  isLoading,
}: {
  holdings: PopulatedAssetHolding[];
  totalHoldingValue: Decimal | undefined;
  isLoading: boolean;
}): React.ReactElement => {
  const { isLoading: marketValueLoading } = useValueHoldings();

  const sortedHoldings = sortHoldings(holdings);

  return (
    <Grid
      w="full"
      templateColumns="1fr 1fr 1fr 1fr max-content"
      overflowX="auto"
      backgroundColor="white"
      borderRadius="md"
      fontSize="sm"
      shadow="sm"
    >
      <HeadingGridCell>
        <Text fontWeight="semibold">Asset</Text>
      </HeadingGridCell>
      <HeadingGridCell>
        <Text fontWeight="semibold">Balance</Text>
      </HeadingGridCell>
      <HeadingGridCell justifyContent="flex-end" textAlign="right">
        <Text fontWeight="semibold">Allocation</Text>
      </HeadingGridCell>
      <HeadingGridCell justifyContent="flex-end" textAlign="right">
        <Text fontWeight="semibold">Current Value</Text>
      </HeadingGridCell>
      <HeadingGridCell />
      {isLoading && (
        <GridCell colSpan={5}>
          <HStack spacing={4}>
            <Spinner size="sm" />
            <Text fontSize="sm">Loading holdings...</Text>
          </HStack>
        </GridCell>
      )}
      {!isLoading && sortedHoldings.length === 0 && (
        <GridCell colSpan={5}>
          <Text>No assets currently held</Text>
        </GridCell>
      )}
      {!isLoading &&
        sortedHoldings.length > 0 &&
        sortedHoldings.map((holding) => (
          <HoldingTableRow
            key={holding.asset._id}
            holding={holding}
            totalHoldingValue={totalHoldingValue}
            marketValueLoading={marketValueLoading}
          />
        ))}
    </Grid>
  );
};

function HoldingTableRow({
  holding,
  totalHoldingValue,
  marketValueLoading,
}: {
  holding: PopulatedAssetHolding;
  totalHoldingValue: Decimal | undefined;
  marketValueLoading: boolean;
}): React.ReactElement {
  const percentageShare =
    (
      holding.marketValue &&
      holding.marketValue.isZeroOrPositive() &&
      totalHoldingValue &&
      !totalHoldingValue.isZero()
    ) ?
      holding.marketValue.div(totalHoldingValue).times(Decimal.HUNDRED)
    : undefined;

  return (
    <>
      <GridCell>
        <Tooltip label={getAssetName(holding.asset)}>
          <HStack spacing={2} alignItems="center">
            <ImageWithMissingSrc boxSize="2rem" src={holding.asset.image} />
            <Text>{getAssetShortName(holding.asset)}</Text>
            <Hide below="lg">
              <Text noOfLines={1}>{getAssetName(holding.asset)}</Text>
            </Hide>
          </HStack>
        </Tooltip>
      </GridCell>

      <GridCell>
        <NumberStyler
          assetInfo={holding.asset}
          num={holding.balance}
          negativeColored
        />
      </GridCell>

      <GridCell justifyContent="flex-end" textAlign="right">
        <SkeletonRect isLoaded={!marketValueLoading}>
          <NumberStyler unit="percentage" num={percentageShare} />
        </SkeletonRect>
      </GridCell>

      <GridCell justifyContent="flex-end" textAlign="right">
        <SkeletonRect isLoaded={!marketValueLoading}>
          <NumberStyler num={holding.marketValue} unit="currency" colored />
        </SkeletonRect>
      </GridCell>

      <GridCell justifyContent="flex-end" textAlign="right">
        {!marketValueLoading && holding.balance.isNonZeroNegative() && (
          <InfoToolTip
            label="A negative balance indicates missing transactions. Please review that you have added your entire transaction history including all deposits and withdrawals."
            wrap
          />
        )}
      </GridCell>
    </>
  );
}

function GridCell(props: GridItemProps) {
  return (
    <GridItem display="flex" px={6} py={2} alignItems="center" {...props} />
  );
}

function HeadingGridCell(props: GridItemProps) {
  return <GridCell borderBottomWidth="1px" borderColor="gray.300" {...props} />;
}
